import axios from 'axios';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Office } from '../../../models/office';
import Title from '../../../components/Title';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import {
  Box,
  Card,
  CardActions,
  Grid,
  Button,
  Divider,
  Typography,
  Checkbox,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Collect } from '../../../models/collect';
import type { Theme } from '@mui/material/styles';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';

const UserCollect = () => {
  useAxiosInterceptor();
  const navigate = useNavigate();
  const { drawerOpen } = useDrawer();
  const { id } = useParams();
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [SelectedCollect, setSelectedCollect] = useState<number[]>([]);
  const [unSelectedCollect, setUnSelectedCollect] = useState<Collect[]>([]);
  const [office_id, setOffice_id] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [collects, setCollects] = useState<Collect[]>([]);

  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const { data } = await axios.get(`useroffice/${id}`);
        setOffices(data.offices);
      } catch (err) {
        console.error('Error fetching offices: ', err);
      }
    };

    fetchOffices();
  }, [id]);

  const GetUserCollect = async (officeId: string) => {
    try {
      const user_id = id;
      const { data } = await axios.get(`usercollect/${officeId}/${user_id}`);
      setCollects(data.collect);
      setSelectedCollect(data.selectedCollect.map((collect: Collect) => collect.id));
      setShowError(false);
      setShowSuccess(false);
      if (data.collect <= 0) {
        setShowError(true);
        setError('No se encuentran registros asociados');
      }
    } catch (err) {
      console.error('Error fetching user offices: ', err);
    }
  };

  const handleGetUserCollect = (event: SelectChangeEvent<string>) => {
    const selectedOfficeId = event.target.value as string;
    setOffice_id(selectedOfficeId);
    GetUserCollect(selectedOfficeId);
  };

  const handleCheckboxChange = (collectId: number, isActive: boolean) => {
    if (isActive) {
      setSelectedCollect((prevSelected: number[]) => [...prevSelected, collectId]);
      setUnSelectedCollect((prevUnselected: Collect[]) =>
        prevUnselected.filter((item) => item.id !== collectId)
      );
    } else {
      setSelectedCollect((prevSelected: number[]) => prevSelected.filter((cid) => cid !== collectId));
      setUnSelectedCollect((prevUnselected: Collect[]) => {
        if (!prevUnselected.map((item) => item.id || item).includes(collectId)) {
          return [...prevUnselected, { id: collectId } as Collect];
        }
        return prevUnselected;
      });
    }
  };

  const handleSelectAll = (isActive: boolean) => {
    if (isActive) {
      const allCollectIds = collects.map((collect) => collect.id);
      setSelectedCollect(allCollectIds);
      setUnSelectedCollect([]);
    } else {
      setSelectedCollect([]);
      setUnSelectedCollect(collects);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const unselectedIds = unSelectedCollect.map((collect) => collect.id);
    const formData = {
      collectId: SelectedCollect,
      setUnselectedCollect: unselectedIds,
      userId: id,
      officeId: office_id,
    };
    try {
      await axios.post('/usercollect', formData);
      navigate(`/administrar/usuarios/${id}/cobros`);
      setSuccess('Registros realizados exitosamente');
      setShowSuccess(true);
    } catch (err) {
      console.error('Error:', err);
      setShowError(true);
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nombre', flex: 1 },
    {
      field: 'options',
      headerName: 'Seleccionar',
      width: 250,
      headerClassName: 'select-all-checkbox-header',
      renderHeader: () => (
        <Checkbox
          indeterminate={
            SelectedCollect.length > 0 && SelectedCollect.length < collects.length
          }
          checked={collects.length > 0 && SelectedCollect.length === collects.length}
          onChange={(event) => handleSelectAll(event.target.checked)}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={SelectedCollect.includes(params.row.id)}
          onChange={(event) => handleCheckboxChange(params.row.id, event.target.checked)}
        />
      ),
    },
  ];

  const rows = collects.map((collect: Collect) => ({
    ...collect,
    name: collect.name,
  }));

  const regionWidthSx = {
    width: (theme: Theme) => stickyListRegionWidth(theme, drawerOpen),
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    overflowX: 'auto' as const,
  };

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Asignar Usuarios a Cobros</Title>
      </StickyListToolbar>
      <Card>
        <CardContent sx={regionWidthSx}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Seleccionar Oficina
              </Typography>
            </Grid>
          </Grid>
          {showError && (
            <Grid container alignItems="center" justifyContent="center" sx={{ my: 1 }}>
              <Grid item xs={12} sm={8} md={5}>
                <Alert severity="error" onClick={() => setShowError(false)}>
                  {error}
                </Alert>
              </Grid>
            </Grid>
          )}
          {showSuccess && (
            <Grid container alignItems="center" justifyContent="center" sx={{ my: 1 }}>
              <Grid item xs={12} sm={8} md={5}>
                <Alert severity="success" onClick={() => setShowSuccess(false)}>
                  {success}
                </Alert>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={5} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <InputLabel id="form-layouts-separator-select-label">Seleccionar Oficina</InputLabel>
                {offices.length > 0 ? (
                  <Select
                    label="Seleccionar Oficina"
                    value={office_id}
                    id="form-layouts-separator-select"
                    labelId="form-layouts-separator-select-label"
                    onChange={handleGetUserCollect}
                    required
                  >
                    {offices.map((office: Office) => (
                      <MenuItem key={office.id} value={office.id}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ marginBottom: 0 }} />
            </Grid>
          </Grid>
        </CardContent>
        <CardContent sx={regionWidthSx}>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ width: '100%' }}>
                {collects.length > 0 ? <DataGrid rows={rows} columns={columns} /> : null}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
        <Divider sx={{ margin: 0 }} />
        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" type="submit" sx={{ mr: 2 }} variant="contained" onClick={submit}>
            Guardar
          </Button>
          <Link to="/administrar/usuarios">
            <Button size="small" color="secondary" variant="outlined">
              Cancelar
            </Button>
          </Link>
        </CardActions>
      </Card>
    </HeaderResponsive>
  );
};

export default UserCollect;
